// src/utils/pendingOrdersFormUtils.tsx
// Utilidades para PendingOrdersForm
// Funciones puras extraídas para mejor organización y reutilización

import React from 'react'
import type { PendingOrderFormData, OrderDetail } from '../types/pendingOrdersFormTypes'

// Tipos para departamentos y ciudades (de los JSONs)
interface Departamento {
  Codigo: number | string
  Nombre: string
}

interface Ciudad {
  codigo: string
  ciudad: string
}

/**
 * Obtiene el nombre de una ciudad por su código
 */
export const getCiudadNombre = (codigo: string, ciudades: Ciudad[]): string => {
  const ciudad = ciudades.find(c => c.codigo === codigo)
  return ciudad ? ciudad.ciudad : codigo
}

/**
 * Obtiene el nombre de un departamento por su código
 */
export const getDepartamentoNombre = (codigo: string, departamentos: Departamento[]): string => {
  const depto = departamentos.find(d => d.Codigo.toString() === codigo)
  return depto ? depto.Nombre : codigo
}

/**
 * Genera las opciones de departamentos para un select
 * Versión específica para PendingOrdersForm (usa nombres en lugar de códigos)
 */
export const renderDepartamentoOptions = (
  selectedDepartamento: string,
  departamentos: Departamento[]
): JSX.Element[] => {
  const options: JSX.Element[] = []
  
  // Si hay un departamento seleccionado, mostrarlo primero
  if (selectedDepartamento) {
    options.push(
      <option key={selectedDepartamento} value={selectedDepartamento}>
        {selectedDepartamento}
      </option>
    )
  }
  
  // Agregar la opción por defecto si no hay departamento seleccionado
  if (!selectedDepartamento) {
    options.push(
      <option key="default" value="">Seleccione un departamento</option>
    )
  }
  
  // Agregar todos los departamentos
  departamentos.forEach((depto) => {
    // Solo agregar si no es el departamento ya seleccionado
    if (depto.Nombre !== selectedDepartamento) {
      options.push(
        <option key={depto.Codigo} value={depto.Nombre}>
          {depto.Nombre}
        </option>
      )
    }
  })
  
  return options
}

/**
 * Genera las opciones de ciudades para un select basándose en el departamento seleccionado
 * Versión específica para PendingOrdersForm (usa nombres en lugar de códigos)
 */
export const renderCiudadOptions = (
  selectedCiudad: string,
  selectedDepartamento: string,
  departamentos: Departamento[],
  ciudades: Ciudad[]
): JSX.Element[] => {
  const options: JSX.Element[] = []
  
  // Obtener el código del departamento
  let codigoDepartamento = ''
  if (selectedDepartamento) {
    const depto = departamentos.find(d => d.Nombre === selectedDepartamento)
    if (depto) {
      codigoDepartamento = depto.Codigo.toString()
    }
  }
  
  // Si hay una ciudad seleccionada, mostrarla primero
  if (selectedCiudad) {
    options.push(
      <option key={selectedCiudad} value={selectedCiudad}>
        {selectedCiudad}
      </option>
    )
  }
  
  // Agregar la opción por defecto si no hay ciudad seleccionada
  if (!selectedCiudad) {
    options.push(
      <option key="default" value="">Seleccione una ciudad</option>
    )
  }
  
  // Filtrar y agregar las ciudades del departamento seleccionado
  ciudades.forEach((ciudad) => {
    // Verificar si la ciudad pertenece al departamento seleccionado
    const perteneceAlDepartamento = codigoDepartamento 
      ? ciudad.codigo.startsWith(codigoDepartamento)
      : true
    
    // Solo agregar si no es la ciudad ya seleccionada y pertenece al departamento
    if (ciudad.ciudad !== selectedCiudad && perteneceAlDepartamento) {
      options.push(
        <option key={ciudad.codigo} value={ciudad.ciudad}>
          {ciudad.ciudad}
        </option>
      )
    }
  })
  
  return options
}

/**
 * Transforma OrderDetail a PendingOrderFormData
 * Inicializa el formulario con los datos del pedido
 */
export const transformOrderDetailToFormData = (
  orderDetail: OrderDetail | null | undefined,
  getCiudadNombreFn: (codigo: string) => string,
  getDepartamentoNombreFn: (codigo: string) => string,
  calculateTotalsFn: (items: any[]) => { total: string }
): PendingOrderFormData => {
  if (!orderDetail) {
    // Valores por defecto si no hay orderDetail
    return {
      origenNombre: 'Mundial de mercancias',
      origenEmpresa: 'Mundial de mercancias',
      origenEmail: 'info@mdm.com.co',
      origenCelular: '3014017136',
      origenPais: 'Colombia',
      origenDepartamento: 'Antioquia',
      origenCiudad: getCiudadNombreFn('05001'),
      origenDireccion: 'Calle 25 A # 43B - 199, Medellin, Antioquia',
      origenReferencia: '',
      origenNit: '9009978173',
      destinoNombre: '',
      destinoEmpresa: '',
      destinoEmail: '',
      destinoCelular: '',
      destinoPais: 'Colombia',
      destinoDepartamento: '',
      destinoCiudad: '',
      destinoDireccion: '',
      destinoDireccion_2: '',
      destinoReferencia: '',
      destinoNit: '',
      contenido: '',
      largo: 0,
      ancho: 0,
      altura: 0,
      peso: 0,
      valorDeclarado: 0,
      recaudoContraEntrega: 0,
      texto_guia: '',
      accion_notaguia: '',
      centrocosto: '',
      valorproducto: '0',
      valorFlete: 0,
      metodoPago: ''
    }
  }

  const totals = calculateTotalsFn(orderDetail.lineItems || [])
  const total_sin_envio = parseFloat(orderDetail.total) - parseFloat(orderDetail.envio || '0')
  const valorProducto = Math.round(parseFloat(totals.total)) || 0
  const destinoDireccionCompleta = orderDetail.shippingAddress.address_1 + 
    (orderDetail.shippingAddress.address_2 ? ', ' + orderDetail.shippingAddress.address_2 : '')

  return {
    origenNombre: orderDetail.store_name || 'Falabella',
    origenEmpresa: 'Mundial de mercancias',
    origenEmail: 'info@mdm.com.co',
    origenCelular: '3014017136',
    origenPais: 'Colombia',
    origenDepartamento: 'Antioquia',
    origenCiudad: getCiudadNombreFn('05001'),
    origenDireccion: 'Calle 25 A # 43B - 199, Medellin, Antioquia',
    origenNit: '9009978173',
    destinoNombre: `${orderDetail.customer.first_name} ${orderDetail.customer.last_name}`,
    destinoEmail: orderDetail.customer.email,
    destinoCelular: orderDetail.shippingAddress.phone || '',
    destinoPais: 'Colombia',
    destinoDepartamento: getDepartamentoNombreFn(orderDetail.shippingAddress.state),
    destinoCiudad: getCiudadNombreFn(orderDetail.shippingAddress.city),
    destinoDireccion: destinoDireccionCompleta,
    destinoDireccion_2: orderDetail.shippingAddress?.address_2 || '',
    destinoReferencia: '',
    destinoNit: orderDetail.customer.document || '',
    contenido: orderDetail.lineItems.map(item => item.product_name).join(', '),
    largo: 0,
    ancho: 0,
    altura: 0,
    peso: 0,
    valorDeclarado: total_sin_envio,
    recaudoContraEntrega: 0,
    texto_guia: '',
    accion_notaguia: orderDetail.extraDetails?.customer_note || '',
    centrocosto: '',
    valorproducto: valorProducto.toString(),
    valorFlete: 0,
    metodoPago: orderDetail.extraDetails?.payment_method || ''
  }
}

